import {matchRoutes, Outlet, useLocation, useNavigate} from "react-router-dom";
import Header from "components/ui/Header";
import Footer from "components/ui/Footer";
import AppInstallBanner from "components/muyeon/AppInstallBanner";
import ScrollTopFab from "components/ui/ScrollTopFab";
import RoleApproved from "pages/muyeon/my/RoleApproved";
import ReceivedQuotesPopup from "pages/muyeon/my/ReceivedQuotesPopup";
import RoleVerifyPrompt from "pages/muyeon/my/RoleVerifyPrompt";
import {privateRouteDefs} from "../routes";
import {useState} from "react";
import {HeaderActionProvider} from "context/HeaderActionContext";
import { requestBackGuard, UnsavedGuardHost } from "hooks/useUnsavedGuard";
import { DraftRestoreHost } from "hooks/useWriteDraft";
import { closeModal } from "common/nativeBridge";
import { isInPaymentSheet } from "util/paymentSheet";
import useTabMenus from "hooks/useTabMenus";

// 무용연 공통 레이아웃.
// (서버 연동 제거: 후니드 QR 식사평가/네이티브 브릿지/MainApi 폴링 로직 삭제)
export default function DefaultLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedFooterMenu, setSelectedFooterMenu] = useState("home");

    // 현재 매칭된 라우트에서 meta 추출
    const matches = matchRoutes([{children: privateRouteDefs}], location) || [];
    const leaf = matches[matches.length - 1]?.route;
    const tabs = useTabMenus();
    /* 탭바에 있을 때만 탭 화면(2026-10-24) — 커뮤니티는 일반 유형에선 하단 탭이지만 다른 유형에선 홈·MY 에서 들어가는
       화면이다. 라우트에 meta.tabWhenInBar 를 두고, 지금 활동유형의 탭 목록에 이 주소가 있으면 다른 탭처럼
       뒤로가기 없이 탭바를 띄운다(상단바 주황, 위로가기 FAB 는 탭바 것을 쓴다). 탭이 아니면 원래 meta 그대로. */
    const baseMeta = leaf?.meta ?? {};
    const path = location.pathname.replace(/\/+$/, "") || "/";
    const asTab = !!baseMeta.tabWhenInBar && tabs.some((t) => t.path === path);
    const meta = asTab ? { ...baseMeta, back: false, backFallback: undefined, mainColor: true, scrollTop: false } : baseMeta;

    const title = typeof meta.title === "function"
      ? meta.title(matches[matches.length - 1], location)
      : (meta.title || "");

    /* 네이티브가 새 창으로 띄운 화면(?embed=1) — 닫기는 네이티브가 한다.
       앱의 약관 '상세보기'가 대표적이다. iOS 는 인앱 사파리 시트, AOS 는 WebViewActivity 로
       이 주소를 **새 창**에 연다. 그 창은 히스토리가 비어 있어 웹 뒤로가기를 누르면
       onBack 의 폴백이 끝까지 떨어져 홈으로 가버렸다.
       ⚠️ 히스토리 길이로 추측하지 않는다 — 브라우저로 이 주소를 직접 연 이용자와 구분이 안 된다.
          그래서 네이티브가 명시적으로 표시를 넘긴다.
       ⚠️ 라우트가 아니라 여기서 처리한다. "네이티브가 감싼 화면"은 특정 화면의 성질이 아니라
          진입 방식의 성질이라, 같은 주소를 웹에서 열면 그대로 뒤로가기가 있어야 한다. */
    //  ⚠️ 라우트가 meta.embeddable 로 허용한 화면에서만 인정한다. 전역으로 두면 아무 주소에나
    //     ?embed=1 을 붙여 뒤로가기·탭바가 없는 화면을 만들 수 있고, 앱 웹뷰에는 브라우저
    //     크롬도 없어 나갈 방법이 사라진다. 지금 이 표시를 붙이는 곳은 네이티브의 약관 상세뿐이다.
    const isEmbedded =
      !!meta.embeddable && new URLSearchParams(location.search).get("embed") === "1";
    // 앱의 예약금 결제 창 안 — 탭바·뒤로가기를 숨기고 나가는 길은 결제 창의 '닫기' 하나로 둔다
    //  (util/paymentSheet.js). 탭바가 뜨면 결제 창을 닫지 않은 채 앱 전체를 돌아다니게 된다.
    const inPaymentSheet = isInPaymentSheet();

    const showBack = isEmbedded || inPaymentSheet ? false : (typeof meta.back === "function"
      ? !!meta.back(matches[matches.length - 1])
      : !!meta.back);

    const isMain = !!meta.isMain;
    const isMyPage = !!meta.isMyPage;
    const mainColor = !!meta.mainColor; // 색상만 주황(상단바), 레이아웃은 title 유지
    const backFallback = typeof meta.backFallback === "function"
      ? meta.backFallback(matches[matches.length - 1], location)
      : meta.backFallback;
    const backTarget = typeof meta.backTarget === "function"
      ? meta.backTarget(matches[matches.length - 1], location)
      : meta.backTarget;

    const onBack = () => {
        // 딥링크(푸시)로 바로 진입하면 히스토리에 이전 항목이 없어 navigate(-1)이 막힌다.
        //  React Router 가 history.state.idx 에 스택 인덱스를 심으므로, 0이면(최상단=뒤로갈 곳 없음)
        //  홈으로 폴백해 항상 이동 가능하게 한다.
        const doBack = () => {
            // 목록에 속한 상세 화면은 진입 경로와 무관하게 해당 목록으로 돌아간다.
            // 알림·딥링크·홈 바로가기에서 열어도 엉뚱한 이전 화면으로 가지 않게 라우트가 명시한다.
            if (backTarget) { navigate(backTarget, { replace: true }); return; }
            const idx = window.history.state?.idx ?? 0;
            if (idx > 0) { navigate(-1); return; }
            // 네이티브에서 작성 화면 URL을 직접 연 경우 웹 히스토리는 0이다. 작성 화면은
            // 라우트에 지정한 논리적 이전 화면으로 먼저 돌아가고, 모달 전체를 닫지 않는다.
            if (backFallback) { navigate(backFallback, { replace: true }); return; }
            // 네이티브 오버레이 모달(허브 위 /myLessons 등)의 루트에서 뒤로가기 → 네이티브 모달을 닫고 허브로 복귀.
            //  (모달은 첫 진입이라 idx=0. window.__nativeModal 은 네이티브가 documentStart 에 주입)
            if (window.__nativeModal && closeModal()) return;
            navigate("/home", { replace: true });
        };
        // 작성 화면 미저장 가드 — dirty 면 확인 모달(가드가 이동을 이어받음)
        if (requestBackGuard(doBack)) return;
        doBack();
    };

    return (
      <HeaderActionProvider>
      <div className={`container has-top-bar${!showBack && !isEmbedded && !inPaymentSheet ? " has-bottom-tab-nav" : ""}`}>
          <Header
            title={title}
            showBack={showBack}
            isMain={isMain}
            isMyPage={isMyPage}
            mainColor={mainColor}
            onBack={onBack}
            action={meta.action}
          />

          <Outlet/>

          {/* 작성 화면 공용 '저장하지 않고 나가기' 확인 모달(단일 인스턴스) */}
          <UnsavedGuardHost />
          {/* 수정 화면 임시저장 불러오기 확인창(단일 인스턴스, hooks/useWriteDraft) */}
          <DraftRestoreHost />

          {/* 하단 탭바는 메인 탭(뒤로가기 없는 최상위)에서만 노출. 상세(back:true)에선 숨김.
              ⚠️ embed 화면은 뒤로가기가 없어도 메인탭이 아니다 — 탭바를 띄우면 네이티브가 띄운
                 약관 창 안에서 앱 전체를 돌아다닐 수 있게 된다. */}
          {!showBack && !isEmbedded && !inPaymentSheet && (
            <Footer
              selectedFooterMenu={selectedFooterMenu}
              setSelectedFooterMenu={setSelectedFooterMenu}
              tabs={tabs}
            />
          )}
          {/* 앱 설치 배너도 하단 탭바가 있는 메인 화면에서만 — 탭바 위 위치를 기준으로 고정돼 있어
              작성 화면의 등록 버튼·상세의 하단 지원 바를 가리지 않게 한다. */}
          {!showBack && !isEmbedded && !inPaymentSheet && <AppInstallBanner />}

          {/* footer(하단 탭바) 없는 페이지의 위로가기 FAB (meta.scrollTop). 커뮤니티 목록 등. */}
          {meta.scrollTop && <ScrollTopFab/>}

          {/* 결제 창 안에서는 다른 화면으로 이끄는 팝업을 띄우지 않는다(탭바를 숨긴 이유와 같다). */}
          {!inPaymentSheet && (
            <>
              {/* 역할 승인 푸시 딥링크(?roleApproved=ROLE) → 메인탭 위로 팝업 오버레이 */}
              <RoleApproved/>
              {/* 받은 견적요청(?quotesPopup=1) → 메인탭 위로 팝업 오버레이 */}
              <ReceivedQuotesPopup/>
              {/* 사업역할 인증 미완료 회원 유도(오늘 하루 그만보기) */}
              <RoleVerifyPrompt/>
            </>
          )}
      </div>
      </HeaderActionProvider>
    );
}
